import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";

export default function UserLogin() {
  const [step, setStep] = useState("brand");
  const [brandInput, setBrandInput] = useState("");
  const [brand, setBrand] = useState(null);
  const [form, setForm] = useState({
    username: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();
  const { login } = useAuth();

  const checkBrand = async (e) => {
    e.preventDefault();

    const slug = brandInput.trim().toLowerCase();
    if (!slug) return;

    setLoading(true);

    try {
      const cacheKey = `tk_brand_${slug}`;
      const cached = sessionStorage.getItem(cacheKey);

      let data;

      if (cached) {
        try {
          data = JSON.parse(cached);
        } catch {
          data = null;
        }
      }

      if (!data) {
        const res = await api.get(`/brand/${slug}`);
        data = res.data;

        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
        } catch (_e) {}
      }

      if (!data.is_active) {
        toast.error("Brand is not active. Please contact your operator.");
        return;
      }

      setBrand(data);
      setStep("creds");
    } catch (err) {
      toast.error(formatError(err) || "Brand not found");
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", {
        username: form.username,
        password: form.password,
        role: "user",
        brand_slug: brand.brand_slug,
      });

      login(data.token, data.user);

      toast.success("Signed in");

      nav("/app");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bet-theme min-h-screen bg-background text-foreground flex flex-col">
      <header className="bet-header">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <ThemeToggle />
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-4 py-10">
        <Card className="w-full max-w-lg md:max-w-md border-border shadow-lg rounded-xl">

          {step === "brand" && (
            <>
              <CardHeader className="pb-2">
                <CardTitle className="font-display text-3xl">
                  Player Login
                </CardTitle>

                <CardDescription className="text-base">
                  Enter your brand code to continue.
                </CardDescription>
              </CardHeader>

              <CardContent className="px-6 pb-6">
                <form
                  onSubmit={checkBrand}
                  className="space-y-5"
                  data-testid="brand-code-form"
                >
                  <div>
                    <Label htmlFor="brand-code">Brand code</Label>

                    <Input
                      className="h-12 mt-2"
                      id="brand-code"
                      data-testid="brand-code-input"
                      required
                      value={brandInput}
                      onChange={(e) => setBrandInput(e.target.value)}
                      placeholder="e.g. royals"
                      autoFocus
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 btn-push"
                    data-testid="brand-code-submit"
                    disabled={loading}
                  >
                    {loading ? "Checking..." : "Continue"}
                  </Button>
                </form>
              </CardContent>
            </>
          )}

          {step === "creds" && brand && (
            <>
              <CardHeader className="pb-2">
                <CardTitle className="font-display text-3xl">
                  Player Login
                </CardTitle>

                <CardDescription className="text-base">
                  Sign in to continue.
                </CardDescription>
              </CardHeader>

              <CardContent className="px-6 pb-6">
                <form
                  onSubmit={submit}
                  className="space-y-5"
                  data-testid="user-login-form"
                >
                  <div>
                    <Label htmlFor="username">Username</Label>

                    <Input
                      className="h-12 mt-2"
                      id="username"
                      data-testid="username-input"
                      required
                      value={form.username}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          username: e.target.value,
                        })
                      }
                      autoFocus
                    />
                  </div>

                  <div>
                    <Label htmlFor="password">Password</Label>

                    <Input
                      className="h-12 mt-2"
                      id="password"
                      type="password"
                      data-testid="password-input"
                      required
                      value={form.password}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          password: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="h-12"
                      onClick={() => {
                        setStep("brand");
                        setBrand(null);
                      }}
                      data-testid="change-brand-btn"
                    >
                      Change Brand
                    </Button>

                    <Button
                      type="submit"
                      className="flex-1 h-12 btn-push"
                      data-testid="login-submit-btn"
                      disabled={loading}
                    >
                      {loading ? "Signing in..." : "Sign In"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </>
          )}

        </Card>
      </main>
    </div>
  );
}
